import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Search, Edit, Trash2, Tag as TagIcon, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { Tag } from '../../types';
import { TagsService } from '../../services';
import ConfirmationModal from '../../components/ConfirmationModal';
import Modal from '../../components/ui/Modal';

const Tags: React.FC = () => {
  const { usuario } = useAuth();
  const [tags, setTags] = useState<Tag[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    tagId: 0,
    tagName: ''
  });
  const [formData, setFormData] = useState({
    nome: '',
    cor: '#3B82F6',
    descricao: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (usuario) {
      loadTags();
    }
  }, [usuario]);

  const loadTags = async () => {
    try {
      setIsLoading(true);
      const response = await TagsService.getTags();
      setTags(response || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      toast.error('Erro ao carregar tags');
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (tagId: number, tagName: string) => {
    setDeleteModal({
      isOpen: true,
      tagId,
      tagName
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      tagId: 0,
      tagName: ''
    });
  };

  const confirmDelete = async () => {
    try {
      await TagsService.deleteTag(deleteModal.tagId);
      toast.success('Tag excluída com sucesso');
      loadTags();
      closeDeleteModal();
    } catch (error: any) {
      console.error('Erro ao excluir tag:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para excluir esta tag');
      } else {
        toast.error('Erro ao excluir tag');
      }
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedTag(null);
    setFormData({
      nome: '',
      cor: '#3B82F6',
      descricao: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (tag: Tag) => {
    setModalMode('edit');
    setSelectedTag(tag);
    setFormData({
      nome: tag.nome || '',
      cor: tag.cor || '#3B82F6',
      descricao: tag.descricao || ''
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTag(null);
    setFormData({
      nome: '',
      cor: '#3B82F6',
      descricao: ''
    });
    setIsSubmitting(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      cor: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    setIsSubmitting(true);

    try {
      if (modalMode === 'create') {
        await TagsService.createTag({
          nome: formData.nome.trim(),
          cor: formData.cor,
          descricao: formData.descricao.trim() || undefined,
        });
        toast.success('Tag criada com sucesso!');
      } else {
        if (selectedTag) {
          await TagsService.updateTag(selectedTag.id, {
            nome: formData.nome.trim(),
            cor: formData.cor,
            descricao: formData.descricao.trim() || undefined
          });
          toast.success('Tag atualizada com sucesso!');
        }
      }

      closeModal();
      loadTags();
    } catch (error: any) {
      console.error('Erro ao salvar tag:', error);
      toast.error(error.message || 'Erro ao salvar tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tag.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Verificar se o usuário tem acesso
  if (!usuario || (usuario.nivel !== 'admin' && usuario.nivel !== 'dev')) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 dark:text-gray-600 text-6xl mb-4">🚫</div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Acesso Negado</h3>
        <p className="text-gray-500 dark:text-gray-400">Apenas administradores e desenvolvedores podem acessar esta página.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Etiquetas
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gerencie as etiquetas para categorização dos tickets
            </p>
            <div className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Total de etiquetas: {tags.length} • Filtradas: {filteredTags.length}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={openCreateModal}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Etiqueta
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar etiquetas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Tags Table */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tag
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tickets
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Data de Criação
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTags.length > 0 ? (
                filteredTags.map((tag) => (
                  <tr key={tag.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div 
                          className="p-1.5 sm:p-2 rounded-lg mr-2 sm:mr-3"
                          style={{ backgroundColor: tag.cor + '20', color: tag.cor }}
                        >
                          <TagIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                        <div className="flex items-center space-x-2">
                          <span 
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: tag.cor + '20', color: tag.cor }}
                          >
                            {tag.nome || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {tag.descricao || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                          {tag.total_tickets || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {tag.created_at ? new Date(tag.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => openEditModal(tag)}
                          className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 p-1"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(tag.id, tag.nome || '')}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1"
                          title="Excluir tag"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-gray-500 dark:text-gray-400">
                    <TagIcon className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-base sm:text-lg font-medium">Nenhuma etiqueta encontrada</p>
                    <p className="text-xs sm:text-sm">Tente ajustar os filtros de busca ou crie uma nova etiqueta</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para criar/editar etiquetas */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={modalMode === 'create' ? 'Nova Etiqueta' : 'Editar Etiqueta'}
          maxWidth="md"
          closeOnBackdropClick={!isSubmitting}
        >
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
            {/* Nome */}
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                Nome *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Digite o nome da etiqueta"
                required
              />
            </div>

            {/* Cor */}
            <div>
              <label htmlFor="cor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                Cor *
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  id="cor"
                  name="cor"
                  value={formData.cor}
                  onChange={handleColorChange}
                  className="w-16 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.cor}
                  onChange={handleColorChange}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="#3B82F6"
                  pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                />
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                Descrição
              </label>
              <textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Digite a descrição da tag (opcional)"
              />
            </div>

            {/* Botões */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 border border-transparent rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </div>
                ) : (
                  modalMode === 'create' ? 'Criar' : 'Salvar'
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja excluir a tag "${deleteModal.tagName}"? Esta ação não pode ser desfeita.`}
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      )}
    </div>
  );
};

export default Tags;

